import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Button, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useAuthStore, usePermissionStore } from '@/stores';

// 布局组件
import { MainLayout } from '@components/layout/MainLayout';
import { AuthLayout } from '@components/layout/AuthLayout';

// 页面组件 - 懒加载
const Login = lazy(() => import('@pages/Auth/Login'));
const Register = lazy(() => import('@pages/Auth/Register'));
const Dashboard = lazy(() => import('@pages/Dashboard'));
const ProjectList = lazy(() => import('@pages/Project/ProjectList'));
const ProjectDetail = lazy(() => import('@pages/Project/ProjectDetail'));
const ProjectEdit = lazy(() => import('@pages/Project/ProjectEdit'));
const TaskDetail = lazy(() => import('@pages/Task/TaskDetail'));
const SubTaskDetail = lazy(() => import('@pages/Task/SubTaskDetail'));
const Board = lazy(() => import('@pages/Board'));
const Gantt = lazy(() => import('@pages/Gantt'));
const Resource = lazy(() => import('@pages/Resource'));
const IssueList = lazy(() => import('@pages/Issue/IssueList'));
const IssueDetail = lazy(() => import('@pages/Issue/IssueDetail'));
const Todo = lazy(() => import('@pages/Todo'));
const ReportCenter = lazy(() => import('@pages/Report/ReportCenter'));
const DailyReport = lazy(() => import('@pages/Report/DailyReport'));
const WeeklyReport = lazy(() => import('@pages/Report/WeeklyReport'));
const BiWeeklyReport = lazy(() => import('@pages/Report/BiWeeklyReport'));
const MonthlyReport = lazy(() => import('@pages/Report/MonthlyReport'));
const Team = lazy(() => import('@pages/Team'));
const Setting = lazy(() => import('@pages/Setting'));
const Profile = lazy(() => import('@pages/Profile'));
const PermissionManager = lazy(() => import('@pages/Admin/PermissionManager'));
const ActivityList = lazy(() => import('@pages/Activity/ActivityList'));
const NotFound = lazy(() => import('@pages/NotFound'));

// 受保护的路由
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();
  
  const hasLocalToken = typeof window !== 'undefined' && !!localStorage.getItem('token');
  
  if (isLoading && !hasLocalToken) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
      </div>
    );
  }
  
  if (!isAuthenticated && !hasLocalToken) {
    const returnUrl = typeof window !== 'undefined' ? window.location.pathname : '';
    if (returnUrl && returnUrl !== '/login') {
      localStorage.setItem('returnUrl', returnUrl);
    }
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// 带权限控制的路由
const PermissionRoute: React.FC<{ children: React.ReactNode; requiredPermission: string }> = ({ children, requiredPermission }) => {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const { hasPermission } = usePermissionStore();
  
  const hasLocalToken = typeof window !== 'undefined' && !!localStorage.getItem('token');
  
  // 如果正在加载且没有本地token，显示加载动画
  if (isLoading && !hasLocalToken) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
      </div>
    );
  }
  
  // 如果没有认证且没有token，跳转登录
  if (!isAuthenticated && !hasLocalToken) {
    const returnUrl = typeof window !== 'undefined' ? window.location.pathname : '';
    if (returnUrl && returnUrl !== '/login') {
      localStorage.setItem('returnUrl', returnUrl);
    }
    return <Navigate to="/login" replace />;
  }
  
  // 如果用户信息还没加载完成，显示加载动画
  if (!user && hasLocalToken) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
      </div>
    );
  }
  
  // 只有在用户信息加载完成后才检查权限
  if (!hasPermission(requiredPermission)) {
    return <Navigate to="/403" replace />;
  }
  
  return <>{children}</>;
};

// 公开路由（已登录用户重定向）
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const hasLocalToken = typeof window !== 'undefined' && !!localStorage.getItem('token');
  
  if (isLoading && hasLocalToken) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
  </div>
);

export const Router: React.FC = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <BrowserRouter basename="/pms">
      <Routes>
      {/* 认证路由 */}
      <Route element={<AuthLayout />}>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
      </Route>

      {/* 主布局路由 */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />

        {/* 动态页面 */}
        <Route path="/activities" element={<ActivityList />} />

        {/* 项目管理 */}
        <Route path="/projects" element={<PermissionRoute requiredPermission="project:view"><ProjectList /></PermissionRoute>} />
        <Route path="/projects/:id" element={<PermissionRoute requiredPermission="project:view"><ProjectDetail /></PermissionRoute>} />
        <Route path="/projects/:id/edit" element={<PermissionRoute requiredPermission="project:edit"><ProjectEdit /></PermissionRoute>} />
        
        {/* 任务管理 - 任务已集成到项目详情页，不单独设入口 */}
        <Route path="/tasks/:id" element={<PermissionRoute requiredPermission="task:view"><TaskDetail /></PermissionRoute>} />
        <Route path="/subtasks/:id" element={<PermissionRoute requiredPermission="task:view"><SubTaskDetail /></PermissionRoute>} />
        
        {/* 核心功能 */}
        <Route path="/board" element={<PermissionRoute requiredPermission="task:view"><Board /></PermissionRoute>} />
        <Route path="/gantt/:projectId" element={<PermissionRoute requiredPermission="project:view"><Gantt /></PermissionRoute>} />
        <Route path="/resources" element={<PermissionRoute requiredPermission="project:view"><Resource /></PermissionRoute>} />
        
        {/* 问题跟踪 */}
        <Route path="/issues" element={<PermissionRoute requiredPermission="task:view"><IssueList /></PermissionRoute>} />
        <Route path="/issues/:id" element={<PermissionRoute requiredPermission="task:view"><IssueDetail /></PermissionRoute>} />
        
        {/* 待办事项 */}
        <Route path="/todos" element={<Todo />} />
        
        {/* 报告中心 */}
        <Route path="/reports" element={<PermissionRoute requiredPermission="report:view"><ReportCenter /></PermissionRoute>} />
        <Route path="/reports/daily" element={<PermissionRoute requiredPermission="report:create"><DailyReport /></PermissionRoute>} />
        <Route path="/reports/weekly" element={<PermissionRoute requiredPermission="report:create"><WeeklyReport /></PermissionRoute>} />
        <Route path="/reports/biweekly" element={<PermissionRoute requiredPermission="report:create"><BiWeeklyReport /></PermissionRoute>} />
        <Route path="/reports/monthly" element={<PermissionRoute requiredPermission="report:create"><MonthlyReport /></PermissionRoute>} />
        
        {/* 团队与设置 */}
        <Route path="/team" element={<PermissionRoute requiredPermission="user:view"><Team /></PermissionRoute>} />
        <Route path="/settings" element={<PermissionRoute requiredPermission="role:manage"><Setting /></PermissionRoute>} />
        <Route path="/profile" element={<Profile />} />
        
        {/* 权限管理 */}
        <Route path="/admin/permissions" element={<PermissionRoute requiredPermission="role:manage"><PermissionManager /></PermissionRoute>} />
        
        {/* 403 权限不足 */}
        <Route path="/403" element={<div className="flex items-center justify-center min-h-screen bg-gray-50"><div className="text-center"><h1 className="text-6xl font-bold text-red-500 mb-4">403</h1><h2 className="text-2xl font-semibold mb-4">权限不足</h2><p className="text-gray-600 mb-8">您没有访问该页面的权限</p><Button type="primary" onClick={() => window.location.href = '/dashboard'}>返回首页</Button></div></div>} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </BrowserRouter>
    </Suspense>
  );
};
